import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryOptions,
} from '@tanstack/react-query';
import type { Id, Paginated, Task, TaskQuery } from '@shared/types';
import { api, ApiError, type TaskPatch } from './api';

/**
 * Query keys are structured so a single write can invalidate exactly what it
 * touched. `keys.tasks()` with no argument is a prefix that matches every
 * task list regardless of filter.
 */
export const keys = {
  me: ['me'] as const,
  projects: (archived: boolean) => ['projects', archived] as const,
  project: (id: Id) => ['project', id] as const,
  buckets: (projectId: Id, viewId: Id) => ['buckets', projectId, viewId] as const,
  tasks: (q?: TaskQuery) => (q ? (['tasks', q] as const) : (['tasks'] as const)),
  task: (id: Id) => ['task', id] as const,
  labels: ['labels'] as const,
  users: ['users'] as const,
};

/** Auth failures must not be retried — they mean "go to the login page". */
function retryUnlessAuth(failureCount: number, error: unknown) {
  if (error instanceof ApiError && (error.isAuthError || error.status === 404)) return false;
  return failureCount < 2;
}

export function useMe() {
  return useQuery({
    queryKey: keys.me,
    queryFn: () => api.me().then((r) => r.user),
    retry: retryUnlessAuth,
    // The session is stable for the whole visit; don't re-check on every mount.
    staleTime: 15 * 60_000,
  });
}

export function useProjects(archived = false) {
  return useQuery({
    queryKey: keys.projects(archived),
    queryFn: () => api.projects(archived),
    retry: retryUnlessAuth,
    staleTime: 5 * 60_000,
  });
}

export function useProject(id: Id | undefined) {
  return useQuery({
    queryKey: keys.project(id ?? 0),
    queryFn: () => api.project(id as Id),
    enabled: id !== undefined,
    retry: retryUnlessAuth,
    staleTime: 5 * 60_000,
  });
}

export function useBuckets(projectId: Id | undefined, viewId: Id | undefined) {
  return useQuery({
    queryKey: keys.buckets(projectId ?? 0, viewId ?? 0),
    queryFn: () => api.buckets(projectId as Id, viewId as Id),
    enabled: projectId !== undefined && viewId !== undefined,
    retry: retryUnlessAuth,
  });
}

export function useTasks(q: TaskQuery, options?: Partial<UseQueryOptions<Paginated<Task>>>) {
  return useQuery({
    queryKey: keys.tasks(q),
    queryFn: () => api.tasks(q),
    retry: retryUnlessAuth,
    // Server-side fan-out is cached for 45s; matching that here avoids
    // pointless refetches when switching between views.
    staleTime: 30_000,
    ...options,
  });
}

/**
 * Task detail incl. comments. `live` re-polls every 15s while the tab is
 * visible so comments from other people appear without a reload (ticket
 * #2053). Drafts are local component state, so a refetch never clobbers them.
 */
export function useTask(id: Id | undefined, live = false) {
  return useQuery({
    queryKey: keys.task(id ?? 0),
    queryFn: () => api.task(id as Id),
    enabled: id !== undefined,
    retry: retryUnlessAuth,
    refetchInterval: live ? 15_000 : false,
  });
}

export function useLabels() {
  return useQuery({
    queryKey: keys.labels,
    queryFn: () => api.labels().then((r) => r.items),
    retry: retryUnlessAuth,
    staleTime: 30 * 60_000,
  });
}

export function useUsers() {
  return useQuery({
    queryKey: keys.users,
    queryFn: () => api.users().then((r) => r.items),
    retry: retryUnlessAuth,
    staleTime: 30 * 60_000,
  });
}

/**
 * Task updates apply optimistically.
 *
 * Ticking a checkbox or dragging a card has to feel instant; the upstream
 * round trip is a fan-out away and can take a moment. On error we roll the
 * cache back to the exact snapshot taken before the mutation.
 */
export function useUpdateTask() {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: ({ id, patch }: { id: Id; patch: TaskPatch }) => api.updateTask(id, patch),

    onMutate: async ({ id, patch }) => {
      // Stop in-flight refetches from clobbering the optimistic value.
      await qc.cancelQueries({ queryKey: keys.tasks() });

      const snapshot = {
        lists: qc.getQueriesData<Paginated<Task>>({ queryKey: keys.tasks() }),
        detail: qc.getQueryData(keys.task(id)),
        buckets: qc.getQueriesData({ queryKey: ['buckets'] }),
      };

      const apply = (t: Task): Task => (t.id === id ? mergePatch(t, patch) : t);

      qc.setQueriesData<Paginated<Task>>({ queryKey: keys.tasks() }, (old) =>
        old ? { ...old, items: old.items.map(apply) } : old,
      );
      // The buckets query returns { buckets, unassigned }; a moved task can be
      // in either, so patch both collections.
      qc.setQueriesData<{ buckets: { tasks: Task[] }[]; unassigned: Task[] }>(
        { queryKey: ['buckets'] },
        (old) =>
          old
            ? {
                ...old,
                buckets: old.buckets.map((b) => ({ ...b, tasks: b.tasks.map(apply) })),
                unassigned: old.unassigned.map(apply),
              }
            : old,
      );
      qc.setQueryData<{ task: Task }>(keys.task(id), (old) =>
        old ? { ...old, task: mergePatch(old.task, patch) } : old,
      );

      return snapshot;
    },

    onError: (_err, _vars, snapshot) => {
      if (!snapshot) return;
      for (const [key, data] of snapshot.lists) qc.setQueryData(key, data);
      for (const [key, data] of snapshot.buckets) qc.setQueryData(key, data);
      if (snapshot.detail !== undefined) {
        qc.setQueryData(keys.task((snapshot.detail as { task: Task }).task.id), snapshot.detail);
      }
    },

    // Always reconcile with the server: it owns derived fields we can't
    // predict (done_at, position renumbering, identifier).
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: keys.tasks() });
      void qc.invalidateQueries({ queryKey: ['buckets'] });
    },
  });
}

/**
 * Applies a wire-shaped patch to a cached Task for the optimistic update.
 * Relation ids can't be resolved to full objects here, so those fields are
 * left alone and corrected by the refetch in onSettled.
 */
function mergePatch(task: Task, patch: TaskPatch): Task {
  const next: Task = { ...task };
  if (patch.title !== undefined) next.title = patch.title;
  if (patch.description !== undefined) next.description = patch.description;
  if (patch.done !== undefined) {
    next.done = patch.done;
    // Mirror the server's behaviour so the UI doesn't flash a stale timestamp.
    next.doneAt = patch.done ? new Date().toISOString() : null;
  }
  if (patch.dueDate !== undefined) next.dueDate = patch.dueDate;
  if (patch.startDate !== undefined) next.startDate = patch.startDate;
  if (patch.endDate !== undefined) next.endDate = patch.endDate;
  if (patch.priority !== undefined) next.priority = patch.priority;
  if (patch.percentDone !== undefined) next.percentDone = patch.percentDone;
  if (patch.bucketId !== undefined) next.bucketId = patch.bucketId;
  if (patch.position !== undefined) next.position = patch.position;
  if (patch.hexColor !== undefined) next.hexColor = patch.hexColor;
  if (patch.isFavorite !== undefined) next.isFavorite = patch.isFavorite;
  return next;
}

export function useCreateTask() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ projectId, title }: { projectId: Id; title: string }) =>
      api.createTask(projectId, title),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.tasks() });
      void qc.invalidateQueries({ queryKey: ['buckets'] });
    },
  });
}

/**
 * Kanban move. Optimistically relocates the card in the buckets cache, then
 * reconciles with the server (which owns done-bucket side effects and
 * positions). Rolls back on error.
 */
export function useMoveTask(viewId: Id) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ taskId, bucketId, beforeTaskId }: { taskId: Id; bucketId: Id | null; beforeTaskId?: Id | null }) =>
      api.moveTask(viewId, taskId, bucketId, beforeTaskId),
    onMutate: async ({ taskId, bucketId, beforeTaskId }) => {
      await qc.cancelQueries({ queryKey: ['buckets'] });
      const snapshot = qc.getQueriesData({ queryKey: ['buckets'] });
      qc.setQueriesData<{ buckets: { id: Id; tasks: Task[] }[]; unassigned: Task[] }>(
        { queryKey: ['buckets'] },
        (old) => {
          if (!old) return old;
          const all = [...old.buckets.flatMap((b) => b.tasks), ...old.unassigned];
          const moved = all.find((t) => t.id === taskId);
          if (!moved) return old;
          const strip = (list: Task[]) => list.filter((t) => t.id !== taskId);
          // Insert `moved` before beforeTaskId (or append if not found), so the
          // optimistic order matches what the server will compute.
          const insert = (list: Task[]) => {
            const base = strip(list);
            const idx = beforeTaskId == null ? -1 : base.findIndex((t) => t.id === beforeTaskId);
            if (idx === -1) return [...base, moved];
            return [...base.slice(0, idx), moved, ...base.slice(idx)];
          };
          return {
            ...old,
            buckets: old.buckets.map((b) => ({
              ...b,
              tasks: b.id === bucketId ? insert(b.tasks) : strip(b.tasks),
            })),
            unassigned: bucketId === null ? insert(old.unassigned) : strip(old.unassigned),
          };
        },
      );
      return { snapshot };
    },
    onError: (_e, _v, ctx) => {
      for (const [key, data] of ctx?.snapshot ?? []) qc.setQueryData(key, data);
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['buckets'] });
      void qc.invalidateQueries({ queryKey: keys.tasks() });
    },
  });
}

export function useUploadAttachment(taskId: Id) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (file: File) => api.uploadAttachment(taskId, file),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.task(taskId) });
    },
  });
}

export function useAddComment(taskId: Id) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (comment: string) => api.addComment(taskId, comment),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.task(taskId) });
    },
  });
}

export function useNotificationPrefs() {
  return useQuery({
    queryKey: ['notification-prefs'],
    queryFn: () => api.notificationPrefs().then((r) => r.prefs),
    retry: retryUnlessAuth,
    staleTime: 5 * 60_000,
  });
}

export function useSetNotificationPrefs() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (prefs: import('./api').NotificationPrefs) => api.setNotificationPrefs(prefs),
    onSuccess: (r) => qc.setQueryData(['notification-prefs'], r.prefs),
  });
}

export function useLogout() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.logout(),
    onSuccess: () => {
      // Drop every cached response — none of it belongs to the next user.
      qc.clear();
    },
  });
}
